import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useNavigate } from 'react-router';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import {
    ApiErrorInterface,
    OperatorLoginDetailFormFields,
    OperatorLoginDetailFormValues,
    OperatorLoginDetailSecurityContent,
    OperatorLoginDetailUserContent,
} from './OperatorLoginDetailForm.tsx';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { OperatorLoginDetailErrorWarning } from './OperatorLoginDetail.tsx';

import { useOperatorLoginListOutlet } from './OperatorLoginsList.tsx';
import { Button, Stack } from '@mui/material';
import { SubmitButton } from '../../ui/buttons/SubmitButton.tsx';
import { ParkingPortalLayoutWithHeader } from '../../mobile/layout/ParkingPortalLayoutWithHeader.tsx';

export function OperatorLoginDetailCreate() {
    const { operatorData, currentLogin } = useOperatorContext();
    const { refetchOperatorLoginList } = useOperatorLoginListOutlet();
    const form = useForm<OperatorLoginDetailFormValues>({
        defaultValues: {
            [OperatorLoginDetailFormFields.username]: '',
            [OperatorLoginDetailFormFields.firstname]: '',
            [OperatorLoginDetailFormFields.lastname]: '',
            [OperatorLoginDetailFormFields.description]: '',
            [OperatorLoginDetailFormFields.email]: '',
            [OperatorLoginDetailFormFields.telnr]: '',
            [OperatorLoginDetailFormFields.sendPasswordLink]: true,
            [OperatorLoginDetailFormFields.allowedPermitTypes]: [],
            [OperatorLoginDetailFormFields.allowedProductTypes]: [],
            [OperatorLoginDetailFormFields.allowedZones]: [],
            [OperatorLoginDetailFormFields.allowedRecodeConditions]: [],
            [OperatorLoginDetailFormFields.allowedParkingaboRoles]: [],
            [OperatorLoginDetailFormFields.permissions]: {
                operatorAppActive: true,
                enforcementAppActive: true,
                parkingpayModule: true,
                taxomexModule: true,
                transactions: true,
                permitsRead: operatorData.licensePlatePermitSettings.active,
                permitsWrite: operatorData.licensePlatePermitSettings.active,
                activityStatement: operatorData.settings.activityStatement,
                whitelistCreate: false,
                whitelistDelete: false,
                userPermissionEdit: false,
                enforcementLog: false,
                syncApp: true,
                usbStickEdit: true,
                parkingMeterEdit: true,
                parkingMeterAlerts: false,
                twintQrCodePairing: false,
                refundParkTransactions: true,
                refundPermits: operatorData.licensePlatePermitSettings.active,
                quotasWrite: false,
                parkingaboInitialBalanceCreate: false,
                cloudConnectorOffstreetDevicesView: true,
                cloudConnectorRecodeTicket: false,
                cloudConnectorAutoRecode: false,
                cloudConnectorAlerts: false,
                cloudConnectorReportingAccess: true,
                masterDataAndStatistics: true,
                parkingaboUserEdit: true,
                eventsRead: false,
                eventsWrite: false,
            },
            [OperatorLoginDetailFormFields.softAnonymizeCheckinPublicPermitAfterDays]:
                currentLogin.maxSoftAnonymizeCheckinPublicPermitAfterDays,
        },
    });

    const [createOperatorLoginState, createOperatorLogin] = useServerWrite<
        OperatorLoginDetailFormValues,
        { organizationLoginId: number },
        ApiErrorInterface | null
    >(() => ({
        url: `/ui-api/operator-account/operator-login`,
        method: RequestMethod.POST,
    }));
    const navigate = useNavigate();
    useServerSuccessEffect(
        createOperatorLoginState,
        ({ organizationLoginId }) => {
            refetchOperatorLoginList();
            navigate(`../operator-login/${organizationLoginId}/general`);
        },
    );
    return (
        <OperatorRoutedModal
            variant={ModalVariant.SMALL}
            backUrl={'..'}
            style={{ maxHeight: '720px' }}
            render={() => (
                <ParkingPortalLayoutWithHeader
                    title={
                        <Localized
                            de="Neuer Benutzer"
                            fr="Nouvel utilisateur"
                            it="Nuovo utente"
                            en="New User"
                        />
                    }
                    backTo={'..'}
                    scrollable
                    padded
                >
                    <OperatorLoginDetailErrorWarning
                        requestState={createOperatorLoginState}
                    />
                    <OperatorLoginCreateContent
                        form={form}
                        maxSoftAnonymizeCheckinPublicPermitAfterDays={
                            currentLogin.maxSoftAnonymizeCheckinPublicPermitAfterDays
                        }
                    />
                    <Stack
                        direction={'row-reverse'}
                        spacing={2}
                        sx={theme => ({ paddingTop: theme.spacing(3) })}
                    >
                        <SubmitButton
                            dirtyForm
                            onClick={form.handleSubmit(createOperatorLogin)}
                            label={
                                <Localized
                                    de="Speichern"
                                    fr="Sauvegarder"
                                    it="Salva"
                                    en="Save"
                                />
                            }
                            loading={
                                createOperatorLoginState.status ===
                                RequestStatus.PENDING
                            }
                        />
                        <Button
                            onClick={() => navigate('..')}
                            variant="outlined"
                            disabled={
                                createOperatorLoginState.status ===
                                RequestStatus.PENDING
                            }
                        >
                            <Localized
                                de="Abbrechen"
                                fr="Annuler"
                                it="Annulla"
                                en="Cancel"
                            />
                        </Button>
                    </Stack>
                </ParkingPortalLayoutWithHeader>
            )}
        />
    );
}

function OperatorLoginCreateContent({
    form,
    maxSoftAnonymizeCheckinPublicPermitAfterDays,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    maxSoftAnonymizeCheckinPublicPermitAfterDays: number;
}) {
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    return (
        <Stack spacing={3}>
            <OperatorLoginDetailUserContent
                form={form}
                readonly={false}
                mandatoryFieldMessage={mandatoryFieldMessage}
                isAdmin={false}
                isCreate
                maxSoftAnonymizeCheckinPublicPermitAfterDays={
                    maxSoftAnonymizeCheckinPublicPermitAfterDays
                }
                emailSuppression={null}
            />
            <OperatorLoginDetailSecurityContent
                form={form}
                readonly={false}
                isDirty={form.formState.isDirty}
                isCreate={true}
                otpEnabled={false}
                operatorLoginId={null}
            />
        </Stack>
    );
}
